import { DocsItem } from "@academy/components"

const link = (href: string, link: string) =>
  `<a href="${href}" class="underline text-blue-600" target="_blank">${link}</a>`

export const POLICY_STATIC: DocsItem[] = [
  {
    title: "1. Introduction",
    content: [
      {
        html: `Kidpreneurs.academy and KidsCrafts.shop (collectively referred to as "we," "our," "us," or the "Academy") are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information and the information of your child(ren) when you visit or use our websites ${link(
          "https://kidpreneurs.academy",
          "https://kidpreneurs.academy",
        )} and ${link("https://kidscrafts.shop", "https://KidsCrafts.shop")} (the "Sites").`,
      },
      {
        list: {
          description: "",
          items: ["Kidpreneurs.academy offers educational classes purchased by parents."],
        },
      },
      {
        list: {
          description: "",
          items: [
            "KidsCrafts.shop is an e-commerce learning and training platform where children, with parental or legal guardian consent and oversight, can prepare item listings for sale, and engage in transactions to practice business skills.",
          ],
        },
      },
    ],
  },
  {
    title: "2. Information We Collect",
    content: [],
  },
  {
    title: "2.1. Personal Information from Parents/Guardians:",
    content: [
      {
        list: {
          description:
            "When you purchase a class on Kidpreneurs.academy, KidsCrafts.shop or create and manage a parent/guardian account for your child on KidsCrafts.shop, we collect:",
          items: ["First and Last Name", "Address", "Email", "Phone"],
        },
      },
    ],
  },
  {
    title: "2.2. Information About Children:",
    content: [
      {
        list: {
          description: "",
          items: [
            "On Kidpreneurs.academy and KidsCrafts.shop: During the purchase process, we collect the first names of the child(ren) who will be attending the purchased classes.",
            "On KidsCrafts.shop: To create an account, we collect information provided by the parent/guardian, which may include:",
          ],
        },
      },
      {
        html: `
          <ul class="ml-8 flex flex-col -mt-2 list-disc">
            <li>A username.</li>
            <li>A store name which should not contain the child's name.  </li>
            <li><b>User-Generated Content:</b> This includes product listings (images, descriptions, parameters, and prices) submitted through the platform's monitored systems.</li>
            <li><b>Transaction Data:</b> Details of sales, purchases, and, if applicable, shipping addresses used for order fulfillment.</li>
          </ul>
        `,
      },
    ],
  },
  {
    title: "2.3. Automatically Collected Information:",
    content: [
      {
        list: {
          description:
            "Like many websites, we automatically collect certain information when you visit our Sites, such as:",
          items: [
            "Log and Usage Data: IP address, browser type, pages visited, and other diagnostic data.",
            "Cookies and Tracking Technologies: We use cookies to enhance your experience, analyze site traffic across our platforms, and understand user preferences. You can control cookie settings through your browser.",
          ],
        },
      },
    ],
  },
  {
    title: "3. How We Use Your Information",
    content: [
      {
        list: {
          description: "We use the information we collect for the following purposes:",
          items: [
            "To Fulfill Your Requests: To process your class registration on Kidpreneurs.academy, KidsCrafts.shop and to operate, maintain, and provide the features of the KidsCrafts.shop marketplace (e.g., displaying user accounts, facilitating transactions).",
            "To Communicate with You: To send you transactional emails, respond to your inquiries, and, with your consent, inform you about future opportunities available on either site.",
            "For Safety and Moderation: To monitor activity on KidsCrafts.shop to ensure a safe, secure, and appropriate environment for all child users. This includes reviewing user-generated content and transactions.",
            "To Improve Our Services: To analyze website usage and trends across both Kidpreneurs.academy and KidsCrafts.shop to improve our websites, workshops, and user experience.",
            "For Security and Compliance: To maintain the safety and security of our services and to comply with legal obligations.",
          ],
        },
      },
    ],
  },
  {
    title: "4. Legal Basis for Processing (For Canadian and International Users)",
    content: [
      {
        list: {
          description: "Our processing of your personal information is based on:",
          items: [
            "Performance of a Contract: Processing is necessary to fulfill the service contract when you purchase a class or use the marketplace features.",
            "Consent: Where required by law (e.g., for marketing communications), we will rely on your explicit consent, which you can withdraw at any time.",
            "Legitimate Interests: We process data for our legitimate interests in improving our services, ensuring network security across our platforms, and moderating the marketplace for safety, provided these interests are not overridden by your rights.",
          ],
        },
      },
    ],
  },
  {
    title: "5. How We Share Your Information",
    content: [
      {
        list: {
          description:
            "We do not sell, trade, or rent your personal information to third parties. We may share information in the following situations:",
          items: [
            "Service Providers: We share data with trusted third-party vendors who assist us in operating our Sites and conducting our business, such as payment processors and email delivery services. These parties are bound by contractual obligations to keep information confidential and use it only for the purposes we disclose.",
            "Legal Obligations: We may disclose information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).",
            "Business Transfers: In the event of a merger, sale, or acquisition of all or a part of our business, your information may be transferred.",
            "Between Our Sites: We may share and process the information you provide across Kidpreneurs.academy and KidsCrafts.shop for the purposes outlined in this Policy, such as customer support and service improvement.",
            "Public Display on KidsCrafts.shop: To operate the marketplace, certain information prepared by a child which has been reviewed and approved by their parent or legal guardian for submission, and subsequently approved by KidsCrafts.shop or its designated review teams or partners - such as product listings, will be visible to other users of the KidsCrafts.shop platform.",
          ],
        },
      },
    ],
  },
  {
    title: "6. Children's Privacy (COPPA and PIPEDA Compliance)",
    content: [
      {
        list: {
          description:
            "We are committed to complying with child privacy laws. Our services are directed to parents, who purchase classes or create and manage marketplace accounts for their children to learn and practice.",
          items: [
            "Verifiable Parental Consent: For KidsCrafts.shop, we require a verifiable parental consent process before an account can be fully activated. This may involve a parent creating the account, verifying their identity, and directly consenting to the terms and data practices.",
            "Parental Control and Rights: You have the right to review the personal information we have collected about your child (including their user-generated content on KidsCrafts.shop), request deletion, and refuse to permit further collection or use of that information. You can manage your account settings and permissions at any time. To exercise these rights, please fill the form Contact us.",
          ],
        },
      },
    ],
  },
  {
    title: "7. Data Security",
    content: [
      {
        html: `We implement appropriate technical and organizational security measures designed to protect your personal information across both ${link(
          "https://kidpreneurs.academy",
          "Kidpreneurs.academy",
        )} and ${link(
          "https://kidscrafts.shop",
          "KidsCrafts.shop",
        )}. This includes moderation and safety protocols for the marketplace. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee its absolute security.`,
      },
    ],
  },
  {
    title: "8. Data Retention",
    content: [
      {
        html: `We will retain your personal information only for as long as is necessary for the purposes set out in this policy. Upon deletion of a ${link(
          "https://kidscrafts.shop",
          "KidsCrafts.shop",
        )} account by a parent, we will permanently delete the profile and user-generated content, retaining only such information as necessary to comply with legal obligations.`,
      },
    ],
  },
  {
    title: "9. International Data Transfers",
    content: [
      {
        html: `Your information is processed primarily in Canada and the US. If we transfer your data to a service provider in another country, we will ensure that appropriate safeguards are in place to protect your data in accordance with this Privacy Policy and applicable law.`,
      },
    ],
  },
  {
    title: "10. Photo and Video Consent",
    content: [
      {
        html: `You, as the parent or legal guardian of the participating child, grant permission to Kidpreneurs.academy to take photographs and/or videos of your child during classes and related activities. <br>
        You understand that these photos and videos may be used for promotional and educational purposes, including publication on Kidpreneurs.academy’s website and social media platforms such as Facebook, Instagram, Twitter, etc. <br>
        You understand that no personal information (such as full name, address, or contact details) will be shared without additional consent. <br>
        This consent is voluntary and may be withdrawn in writing at any time.`,
      },
    ],
  },
  {
    title: "11. Your Privacy Rights",
    content: [
      {
        list: {
          description: "Depending on your location, you may have the right to:",
          items: [
            "Access and receive a copy of your personal information.",
            "Correct inaccurate or incomplete data.",
            "Request the deletion of your personal information.",
            "Object to or restrict our processing of your data.",
            "Withdraw your consent at any time, where processing is based on consent. To exercise any of these rights, please contact us using the form Contact us.",
          ],
        },
      },
    ],
  },
  {
    title: "12. Changes to This Privacy Policy",
    content: [
      {
        html: `We reserve the right to make changes to this Privacy Policy at our discretion. Any changes will be posted on our Sites, and you will have the opportunity to review the updated version. We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.`,
      },
    ],
  },
  {
    title: "13. Contact Us",
    content: [
      {
        html: `If you have any questions about our Privacy Policy, please fill the form ${link(
          "/contacts",
          "Contact us",
        )}.`,
      },
    ],
  },
]
